import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { departments } from "./data";
import { getDeptLogo, portalHost, paymentDirectUrl, paymentPortalUrl } from "./deptHelpers";
import { UI, t, deptText, payText, paymentSearchText } from "./i18n";
import { LEGAL } from "./legalContent";
import { LogoWordmark } from "./Logo";
import { updatePageMeta } from "./seo";
import { useAuth, LoginPage, SignupPage, ForgotPage, ResetPage, VerifyPage, AccountPage } from "./AuthPages";
import { GuidesIndexPage, PaymentGuidePage, CityHubPage, getGuideSeo, getCitySeo } from "./SeoRoutes";
import { FEATURED_CORPORATIONS, getPaymentBySlug, getCityBySlug } from "./seoPages";
import { ShareBar, SeasonalBanner } from "./share";
import "./App.css";
import {
  Zap, Building2, Trees, Receipt, Car, Stethoscope, BookOpen,
  GraduationCap, HardHat, Factory, Shield, Scale, FileText,
  Landmark, Banknote, Home, Sprout, ShoppingBasket, Wrench,
  Route, TreePine, Monitor, Scissors, Construction, Droplets,
  Search, X, ExternalLink, ArrowUpRight, ShieldCheck,
  Fish, Trophy, Heart, Accessibility, Users, UserCheck, Gem,
  Briefcase, Target, Languages, HandHeart, Vote,
} from "lucide-react";

const LANG_KEY = "indiafees-lang";
const LEGAL_PAGES = new Set(Object.keys(LEGAL));
const AUTH_VIEWS = new Set(["login", "signup", "forgot", "reset", "verify", "account"]);

function QuickPayHero({ lang, items }) {
  return (
    <div className="if-hero-quickpay">
      <p className="if-hero-quickpay-label">{t(lang, "quickPay")}</p>
      <div className="if-hero-quickpay-grid" role="list">
        {items.map((item) => {
          const slug = item.sharePath?.replace(/^\/pay\//, "");
          const entry = slug ? getPaymentBySlug(slug) : null;
          const url = entry?.payment?.url;
          return (
            <button
              key={item.label}
              type="button"
              className="if-qp-chip"
              role="listitem"
              onClick={() => url && window.open(url, "_blank", "noopener,noreferrer")}
              disabled={!url}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DEPT_ICONS = {
  1: Zap, 2: Building2, 3: Trees, 4: Receipt, 5: Car,
  6: Stethoscope, 7: BookOpen, 8: GraduationCap, 9: HardHat,
  10: Factory, 11: Shield, 12: Scale, 13: FileText, 14: Landmark,
  15: Banknote, 16: Home, 17: Sprout, 18: ShoppingBasket, 19: Wrench,
  20: Route, 21: TreePine, 22: Monitor, 23: Scissors, 24: Construction,
  25: Droplets, 26: Fish, 27: Trophy, 28: Heart, 29: Accessibility,
  30: Users, 31: UserCheck, 32: Gem, 33: Briefcase, 34: Target,
  35: Languages, 36: HandHeart, 37: Vote,
};

const allPayments = departments.flatMap((d) =>
  d.payments.map((p, index) => ({ ...p, dept: d.dept, deptId: d.id, payIndex: index }))
);

function getLang() {
  try {
    const legacy = localStorage.getItem("if-lang") || localStorage.getItem("ur-lang");
    const current = localStorage.getItem(LANG_KEY);
    const s = current || legacy;
    if (!current && legacy) localStorage.setItem(LANG_KEY, legacy);
    return s === "ta" ? "ta" : "en";
  } catch {
    return "en";
  }
}

function parseRoute(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "guides") return { view: "guides" };
  if (parts[0] === "pay" && parts[1]) return { view: "pay", slug: parts[1] };
  if (parts[0] === "city" && parts[1]) return { view: "city", slug: parts[1] };
  if (parts[0] === "login") return { view: "login" };
  if (parts[0] === "signup") return { view: "signup" };
  if (parts[0] === "forgot-password") return { view: "forgot" };
  if (parts[0] === "reset-password") return { view: "reset" };
  if (parts[0] === "verify") return { view: "verify" };
  if (parts[0] === "account") return { view: "account" };
  if (parts[0] === "dept" && parts[1]) {
    const id = Number(parts[1]);
    const dept = departments.find((d) => d.id === id);
    return dept ? { view: "dept", dept } : { view: "invalid" };
  }
  if (parts[0] === "legal" && LEGAL_PAGES.has(parts[1])) {
    return { view: "legal", page: parts[1] };
  }
  if (parts[0] === "search") return { view: "search" };
  if (parts.length === 0) return { view: "home" };
  return { view: "invalid" };
}

function Medallion({ dept, size = "md" }) {
  const logo = getDeptLogo(dept);
  const [logoFailed, setLogoFailed] = useState(false);
  const Icon = DEPT_ICONS[dept.id];
  const sz = size === "xl" ? "med med--xl" : size === "lg" ? "med med--lg" : "med";
  const showLogo = logo && !logoFailed;

  return (
    <div className={sz} style={{ "--tint": dept.color, "--ink": dept.iconColor }}>
      {showLogo ? (
        <img src={logo} alt="" onError={() => setLogoFailed(true)} />
      ) : Icon ? (
        <Icon strokeWidth={1.75} />
      ) : null}
    </div>
  );
}

function DeptCard({ dept, lang, onOpen }) {
  const d = deptText(dept, lang);
  return (
    <button type="button" className="dept-card" onClick={() => onOpen(dept)}>
      <Medallion dept={dept} />
      <div className="dept-card-body">
        <span className="dept-card-name">{d.short}</span>
        <span className="dept-card-meta">{t(lang, "paymentLinks", dept.payments.length)}</span>
      </div>
      <ArrowUpRight className="dept-card-arrow" size={16} aria-hidden="true" />
    </button>
  );
}

function PayCard({ payment, lang, showMinistry }) {
  const dept = departments.find((d) => d.id === payment.deptId);
  const Icon = DEPT_ICONS[payment.deptId];
  const [logoFailed, setLogoFailed] = useState(false);
  const pt = payText(payment, payment.deptId, payment.payIndex ?? 0, lang);
  const dt = dept && deptText(dept, lang);
  const host = portalHost(payment.url);
  const label = `${pt.name} — ${payment.portal}${host ? ` (${host})` : ""} (${t(lang, "opensNewTab")})`;

  return (
    <a
      className="paycard"
      href={payment.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
    >
      <div className="paycard-head">
        <div className="paycard-mark">
          {payment.logo && !logoFailed ? (
            <img src={payment.logo} alt="" onError={() => setLogoFailed(true)} />
          ) : (
            <span
              className="paycard-mark-fallback"
              style={dept ? { background: dept.color, color: dept.iconColor } : undefined}
            >
              {Icon ? <Icon size={18} /> : <ExternalLink size={16} />}
            </span>
          )}
        </div>
        {showMinistry && dt && <span className="paycard-dept">{dt.short}</span>}
      </div>
      <h3 className="paycard-title">{pt.name}</h3>
      <p className="paycard-desc">{pt.desc}</p>
      <div className="paycard-foot">
        <span className="paycard-portal">
          <ShieldCheck size={12} aria-hidden="true" />
          {payment.portal}
          {host && <span className="paycard-host">{host}</span>}
        </span>
        <span className="paycard-cta">
          {t(lang, "openPortal")}
          <ArrowUpRight size={14} aria-hidden="true" />
        </span>
      </div>
    </a>
  );
}

const FOOT_KEYS = {
  privacy: "footPrivacy",
  terms: "footTerms",
  contact: "footContact",
  disclaimer: "footDisclaimer",
  hyperlinking: "footHyperlink",
};

function LegalPage({ page, lang, onBack }) {
  const doc = LEGAL[page]?.[lang] || LEGAL[page]?.en;
  if (!doc) return null;

  return (
    <article className="if-legal">
      <button type="button" className="if-legal-back" onClick={onBack}>
        ← {t(lang, "home")}
      </button>
      <header className="if-legal-head">
        <h1>{doc.title}</h1>
        <p>{doc.updated}</p>
      </header>
      <div className="if-legal-body">
        {doc.sections.map((sec) => (
          <section key={sec.heading}>
            <h2>{sec.heading}</h2>
            <p>{sec.body}</p>
          </section>
        ))}
      </div>
    </article>
  );
}

function SiteFooter({ lang, onLegal, onGuides }) {
  const linkBtn = (page) => (
    <button type="button" className="if-foot-link" onClick={() => onLegal(page)}>
      {t(lang, FOOT_KEYS[page])}
    </button>
  );

  return (
    <footer className="if-foot">
      <div className="if-foot-trust">
        <ShieldCheck size={18} aria-hidden="true" />
        <div>
          <strong>{t(lang, "footTrustTitle")}</strong>
          <p>{t(lang, "footTrustBody")}</p>
        </div>
      </div>

      <div className="if-foot-links">
        <nav className="if-foot-nav" aria-label="Policies">
          <div className="if-foot-row if-foot-row--policies">
            {linkBtn("privacy")}
            <span className="if-foot-sep" aria-hidden="true">|</span>
            {linkBtn("terms")}
            <span className="if-foot-sep" aria-hidden="true">|</span>
            {linkBtn("disclaimer")}
            <span className="if-foot-sep" aria-hidden="true">|</span>
            {linkBtn("hyperlinking")}
          </div>
        </nav>

        <nav className="if-foot-extra" aria-label="Site links">
          <div className="if-foot-row if-foot-row--extra">
            <button type="button" className="if-foot-link" onClick={onGuides}>{t(lang, "footGuides")}</button>
            <span className="if-foot-sep" aria-hidden="true">|</span>
            {linkBtn("contact")}
          </div>
        </nav>
      </div>

      <p className="if-foot-copy">{t(lang, "footCopy", new Date().getFullYear())}</p>
    </footer>
  );
}

function SearchBox({ lang, query, onSearch, onClear, id = "if-search", large }) {
  const [focused, setFocused] = useState(false);
  const label = t(lang, "searchPlaceholder");
  const showInBoxLabel = !query && !focused;

  return (
    <div className={`if-search-block${large ? " if-search-block--lg" : ""}`}>
      <label
        className={`if-center-search${large ? " if-center-search--lg" : ""}`}
        htmlFor={id}
      >
        <Search size={large ? 20 : 18} strokeWidth={2} aria-hidden="true" />
        <div className="if-search-field">
          {showInBoxLabel && (
            <span className="if-search-inbox" aria-hidden="true">{label}</span>
          )}
          <input
            id={id}
            type="search"
            value={query}
            onChange={onSearch}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-label={label}
            autoComplete="off"
            spellCheck="false"
            enterKeyHint="search"
          />
        </div>
        {query && (
          <button type="button" className="if-search-x" onClick={onClear} aria-label={t(lang, "clear")}>
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState(getLang);
  const { user, loading: authLoading, refresh, signOut, setUser } = useAuth();

  const route = parseRoute(location.pathname);
  const query = searchParams.get("q") || "";
  const view = route.view === "search" && !query.trim() ? "home" : route.view;
  const activeDept = route.view === "dept" ? route.dept : null;
  const legalPage = route.view === "legal" ? route.page : null;

  useEffect(() => {
    if (route.view === "invalid") navigate("/", { replace: true });
  }, [route.view, navigate]);

  useEffect(() => {
    if (location.pathname === "/" && query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`, { replace: true });
    }
  }, [location.pathname, query, navigate]);

  useEffect(() => {
    document.documentElement.lang = lang === "ta" ? "ta" : "en";
    try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (view === "dept" && activeDept) {
      const d = deptText(activeDept, lang);
      updatePageMeta({
        title: d.short,
        description: t(lang, "deptMetaDesc", d.dept, activeDept.payments.length),
        path,
      });
    } else if (view === "pay" && route.slug) {
      const seo = getGuideSeo(route.slug, lang);
      if (seo) updatePageMeta({ title: seo.title.replace(/ \(2026\)$/, "").replace(/ — .*$/, ""), description: seo.description, path: seo.path });
    } else if (view === "city" && route.slug) {
      const seo = getCitySeo(route.slug, lang);
      if (seo) updatePageMeta({ title: seo.title, description: seo.description, path: seo.path });
    } else if (view === "guides") {
      updatePageMeta({
        title: t(lang, "guidesTitle"),
        description: t(lang, "guidesSub", allPayments.length),
        path: "/guides",
      });
    } else if (view === "legal" && legalPage) {
      updatePageMeta({
        title: t(lang, FOOT_KEYS[legalPage]),
        path,
      });
    } else if (view === "search" && query) {
      updatePageMeta({
        title: `Search: ${query}`,
        description: `Find official government payment links for “${query}” on IndiaFees.`,
        path,
      });
    } else {
      updatePageMeta({ title: null, path: "/" });
    }
  }, [view, activeDept, legalPage, query, lang, location.pathname, location.search, route.slug, route.view]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return allPayments.filter((p) => {
      const dept = departments.find((d) => d.id === p.deptId);
      return paymentSearchText(p, dept, p.deptId, p.payIndex).includes(q);
    });
  }, [query]);

  const onSearch = (e) => {
    const q = e.target.value;
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q)}`, { replace: true });
    } else {
      navigate("/");
    }
  };

  const openDept = (dept) => {
    navigate(`/dept/${dept.id}`);
    window.scrollTo(0, 0);
  };

  const openLegal = (page) => {
    navigate(`/legal/${page}`);
    window.scrollTo(0, 0);
  };

  const home = () => {
    navigate("/");
    window.scrollTo(0, 0);
  };

  const quickPay = UI[lang].quickPayItems;
  const openGuide = (slug) => {
    navigate(`/pay/${slug}`);
    window.scrollTo(0, 0);
  };

  const isAuthView = AUTH_VIEWS.has(view);

  const crumbLabel = view === "legal" && legalPage
    ? t(lang, FOOT_KEYS[legalPage])
    : view === "guides"
      ? t(lang, "guidesTitle")
      : view === "pay" && route.slug
        ? (getGuideSeo(route.slug, lang)?.title?.split(" — ")[0] || route.slug)
        : view === "city" && route.slug
          ? (() => {
            const city = getCityBySlug(route.slug);
            const name = city ? (lang === "ta" ? city.nameTa : city.name) : route.slug;
            return t(lang, "cityCrumb", name);
          })()
          : activeDept && deptText(activeDept, lang).short;

  return (
    <div className="if">
      <a href="#main" className="if-skip">{t(lang, "skipToMain")}</a>

      <header className="if-header">
        <div className="if-tricolor" aria-hidden="true" />
        <div className="if-header-inner">
          <button type="button" className="if-brand" onClick={home} aria-label={t(lang, "homeBrand")}>
            <LogoWordmark />
          </button>

          {(view === "dept" && activeDept) || (view === "legal" && legalPage) || view === "guides" || view === "pay" || view === "city" ? (
            <nav className="if-header-crumb" aria-label="Breadcrumb">
              <button type="button" onClick={home}>{t(lang, "home")}</button>
              <span aria-hidden="true">/</span>
              <span className="if-header-crumb-current" aria-current="page">{crumbLabel}</span>
            </nav>
          ) : null}

          <div className="if-header-actions">
            <div className="if-auth-head">
              {!authLoading && (
                user ? (
                  <>
                    <button type="button" className="if-auth-head-btn" onClick={() => navigate("/account")}>
                      {user.name || t(lang, "authAccount")}
                    </button>
                    <button type="button" className="if-auth-head-btn if-auth-head-btn--ghost" onClick={async () => { await signOut(); navigate("/"); }}>
                      {t(lang, "authLogout")}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="if-auth-head-btn if-auth-head-btn--ghost" onClick={() => navigate("/login")}>
                      {t(lang, "authLogin")}
                    </button>
                    <button type="button" className="if-auth-head-btn" onClick={() => navigate("/signup")}>
                      {t(lang, "authSignup")}
                    </button>
                  </>
                )
              )}
            </div>
            <div className="if-lang" role="group" aria-label="Language">
              <button
                type="button"
                className={`if-lang-btn${lang === "en" ? " if-lang-btn--on" : ""}`}
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
              >
                English
              </button>
              <button
                type="button"
                className={`if-lang-btn${lang === "ta" ? " if-lang-btn--on" : ""}`}
                onClick={() => setLang("ta")}
                aria-pressed={lang === "ta"}
              >
                தமிழ்
              </button>
            </div>
          </div>
        </div>
      </header>

      {view === "home" && (
        <section className="if-hero">
          <div className="if-hero-inner">
            <h1 className="if-hero-title">{t(lang, "heroTitle")}</h1>

            <SearchBox
              lang={lang}
              query={query}
              onSearch={onSearch}
              onClear={home}
              id="if-hero-search"
              large
            />

            <QuickPayHero lang={lang} items={quickPay} />
            <ShareBar lang={lang} path="/" compact messageKey="viralBannerMsg" />
          </div>
        </section>
      )}

      {view === "home" && <SeasonalBanner lang={lang} />}

      <main id="main" className={`if-main${isAuthView ? " if-main--auth" : ""}`}>
        {view !== "home" && view !== "legal" && view !== "guides" && view !== "pay" && view !== "city" && !isAuthView && (
          <section className="if-search-strip" aria-label={t(lang, "searchPlaceholder")}>
            <SearchBox
              lang={lang}
              query={query}
              onSearch={onSearch}
              onClear={home}
              id="if-page-search"
              large
            />
          </section>
        )}

        {view === "search" && (
          <section className="if-block">
            <header className="if-block-head">
              <h2>{t(lang, "results", results.length)}</h2>
              <p>{t(lang, "resultsFor", query)}</p>
            </header>
            {results.length === 0 ? (
              <div className="if-empty">
                <Search size={28} strokeWidth={1.25} aria-hidden="true" />
                <p>{t(lang, "emptySearch")}</p>
              </div>
            ) : (
              <div className="if-paygrid">
                {results.map((p, i) => (
                  <PayCard key={`${p.url}-${i}`} payment={p} lang={lang} showMinistry />
                ))}
              </div>
            )}
          </section>
        )}

        {view === "dept" && activeDept && (
          <section className="if-deptview">
            <header
              className="if-deptbanner"
              style={{ "--banner": activeDept.color, "--banner-ink": activeDept.iconColor }}
            >
              <Medallion dept={activeDept} size="xl" />
              <div className="if-deptbanner-text">
                <p className="if-deptbanner-tag">{deptText(activeDept, lang).minister}</p>
                <h1>{deptText(activeDept, lang).short}</h1>
                <p className="if-deptbanner-sub">{deptText(activeDept, lang).dept}</p>
                <span className="if-deptbanner-count">
                  {t(lang, "paymentLinks", activeDept.payments.length)}
                </span>
              </div>
            </header>

            <ShareBar
              lang={lang}
              path={`/dept/${activeDept.id}`}
              messageKey="shareMsgDept"
              msgName={deptText(activeDept, lang).short}
              title={deptText(activeDept, lang).short}
            />

            <div className="if-paygrid">
              {activeDept.payments.map((p, i) => (
                <div key={i} className="if-paycard-wrap">
                  <PayCard payment={{ ...p, deptId: activeDept.id, payIndex: i }} lang={lang} />
                  <div className="if-paycard-actions">
                    <a
                      className="if-guide-link-btn"
                      href={paymentDirectUrl(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(lang, "guideLink")}
                    </a>
                    <a
                      className="if-guide-link-btn if-guide-link-btn--primary"
                      href={paymentPortalUrl(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(lang, "openPortal")}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {view === "guides" && (
          <GuidesIndexPage lang={lang} onOpenGuide={openGuide} />
        )}

        {view === "pay" && route.slug && (
          <PaymentGuidePage lang={lang} slug={route.slug} onBack={() => navigate("/guides")} />
        )}

        {view === "city" && route.slug && (
          <CityHubPage lang={lang} slug={route.slug} onOpenDept={openDept} />
        )}

        {view === "legal" && legalPage && (
          <LegalPage page={legalPage} lang={lang} onBack={home} />
        )}

        {view === "login" && (
          <LoginPage lang={lang} onSuccess={(u) => { setUser(u); refresh(); }} />
        )}
        {view === "signup" && (
          <SignupPage lang={lang} onSuccess={(u) => { setUser(u); refresh(); }} />
        )}
        {view === "forgot" && <ForgotPage lang={lang} />}
        {view === "reset" && <ResetPage lang={lang} />}
        {view === "verify" && <VerifyPage lang={lang} />}
        {view === "account" && (
          <AccountPage lang={lang} user={user} onLogout={signOut} />
        )}

        {view === "home" && (
          <>
            <section className="if-section">
              <header className="if-section-head">
                <h2>{t(lang, "cityBrowse")}</h2>
              </header>
              <div className="if-citygrid">
                {FEATURED_CORPORATIONS.map((city) => (
                  <button
                    key={city.slug}
                    type="button"
                    className="if-citychip"
                    onClick={() => { navigate(`/city/${city.slug}`); window.scrollTo(0, 0); }}
                  >
                    {lang === "ta" ? (
                      <>
                        <span className="if-citychip-name">
                          {city.nameTa} மாநகராட்சி
                        </span>
                        <span className="if-citychip-sub">{city.name}</span>
                      </>
                    ) : (
                      <span className="if-citychip-name">
                        {city.name}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </section>

            <section className="if-section">
              <header className="if-section-head">
                <h2>{t(lang, "browseDept")}</h2>
                <p>{t(lang, "browseDeptSub", departments.length)}</p>
              </header>

              <div className="if-deptgrid">
                {departments.map((d) => (
                  <DeptCard key={d.id} dept={d} lang={lang} onOpen={openDept} />
                ))}
              </div>
            </section>
          </>
        )}
      </main>

      <div className="if-foot-wrap">
        <SiteFooter lang={lang} onLegal={openLegal} onGuides={() => { navigate("/guides"); window.scrollTo(0, 0); }} />
      </div>
    </div>
  );
}
